import { logWarning } from '@dx-ui/framework-logger';
import type * as React from 'react';
import { useEffect, useMemo } from 'react';
import { AuthContext, initialContext } from './auth-context';
import type { GuestQuery } from './gql/types';
import { ServerFirstAuthClient } from './server-first-auth-client';
import type { AuthClientLike } from './types';

// Same key and value AuthProvider uses, so tabs of either provider stay in sync.
const AUTH_SYNC_KEY = 'auth_sync';
const AUTH_SYNC_LOGOUT = 'logout';

function broadcastLogout() {
  try {
    // Remove first so the write always fires a storage event, even if a stale value was left behind.
    window.localStorage.removeItem(AUTH_SYNC_KEY);
    window.localStorage.setItem(AUTH_SYNC_KEY, AUTH_SYNC_LOGOUT);
    setTimeout(() => window.localStorage.removeItem(AUTH_SYNC_KEY), 100);
  } catch (error) {
    logWarning('AUTH_BROADCAST_LOGOUT', error as Error);
  }
}

/**
 * Provides server-resolved auth state to `useAuth()` consumers (e.g. OSC components).
 *
 * Unlike `AuthProvider`, it never fetches guest info client-side, so the value is
 * identical on the server and client and is available during SSR.
 */
export function ServerAuthProvider({
  authClient = null,
  children,
  guestInfo,
  isAuthenticated,
  onCrossTabLogout,
}: {
  authClient?: AuthClientLike | null;
  children: React.ReactNode;
  guestInfo: GuestQuery['guest'] | null;
  isAuthenticated: boolean;
  /** Called after another tab signs out and this tab's client session data is cleared (e.g. reload). */
  onCrossTabLogout?: () => void;
}) {
  const value = useMemo(
    () => ({
      ...initialContext,
      authClient,
      guestInfo,
      isAuthenticated,
      guestId: guestInfo?.guestId ?? null,
      logout: () => authClient?.logout() ?? Promise.resolve(),
    }),
    [authClient, guestInfo, isAuthenticated]
  );

  useEffect(() => {
    if (!authClient) return;
    authClient.on('logout', broadcastLogout);
    return () => {
      authClient.off('logout', broadcastLogout);
    };
  }, [authClient]);

  useEffect(() => {
    function handleStorage(event: StorageEvent) {
      if (event.key !== AUTH_SYNC_KEY || event.newValue !== AUTH_SYNC_LOGOUT) return;
      // Clear without emitting `logout`, which would re-broadcast.
      if (authClient instanceof ServerFirstAuthClient) authClient.clearClientSessionData();
      onCrossTabLogout?.();
    }
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [authClient, onCrossTabLogout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
